import React, { useState, useEffect } from 'react';
import { apiFetch, getApiUrl } from '../../config/api';
import { useTranslation } from 'react-i18next';
import './SalesCostingOverview.css'; // Reuse same styling

const ProductionCostingOverview = () => {
  const { t } = useTranslation('production');
  const [overview, setOverview] = useState(null);
  const [orderCosts, setOrderCosts] = useState([]);
  const [wasteCosts, setWasteCosts] = useState(null);
  const [topDrivers, setTopDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const periods = [
    { value: 'today', label: t('costing.period.today') },
    { value: 'week', label: t('costing.period.week') },
    { value: 'month', label: t('costing.period.month') },
    { value: 'quarter', label: t('costing.period.quarter') },
    { value: 'year', label: t('costing.period.year') },
    { value: 'all', label: t('costing.period.all') },
  ];

  useEffect(() => {
    fetchData();
  }, [selectedPeriod]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const qs = `?period=${selectedPeriod}`;

      const [overviewRes, ordersRes, wasteRes, driversRes] = await Promise.all([
        apiFetch(getApiUrl(`/api/production-costs/overview${qs}`)),
        apiFetch(getApiUrl(`/api/production-costs/by-order${qs}`)),
        apiFetch(getApiUrl(`/api/production-costs/waste${qs}`)),
        apiFetch(getApiUrl(`/api/production-costs/top-drivers${qs}`)),
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (ordersRes.ok) setOrderCosts(await ordersRes.json());
      if (wasteRes.ok) setWasteCosts(await wasteRes.json());
      if (driversRes.ok) setTopDrivers(await driversRes.json());
    } catch (err) {
      console.error('Error fetching production costs:', err);
      setError(t('productionCostingOverview.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => {
    const n = parseFloat(v) || 0;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtPct = (v) => `${(parseFloat(v) || 0).toFixed(1)}%`;

  if (loading) {
    return (
      <div className="sales-costing-overview loading">
        <div className="loading-spinner" />
        <p>{t('productionCostingOverview.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sales-costing-overview error">
        <p>{error}</p>
        <button className="btn-primary" onClick={fetchData}>{t('costing.retry')}</button>
      </div>
    );
  }

  return (
    <div className="sales-costing-overview">
      {/* Period Selector */}
      <div className="period-selector">
        <div className="preset-periods">
          {periods.map((p) => (
            <button
              key={p.value}
              className={`period-btn ${selectedPeriod === p.value ? 'active' : ''}`}
              onClick={() => setSelectedPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      {overview && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-label">{t('productionCostingOverview.totalMaterialCost')}</div>
            <div className="card-value">{fmt(overview.total_material_cost)}</div>
            <div className="card-detail">{t('productionCostingOverview.unitsProduced', { count: overview.total_units_produced || 0 })}</div>
          </div>
          <div className="summary-card">
            <div className="card-label">{t('productionCostingOverview.wasteCost')}</div>
            <div className="card-value" style={{ color: parseFloat(overview.total_waste_cost || 0) > 0 ? 'var(--status-error, #f44336)' : 'inherit' }}>
              {fmt(overview.total_waste_cost)}
            </div>
            <div className="card-detail">{t('productionCostingOverview.ofMaterial', { value: fmtPct(overview.waste_percentage) })}</div>
          </div>
          <div className="summary-card">
            <div className="card-label">{t('productionCostingOverview.productionOrders')}</div>
            <div className="card-value">{new Set(orderCosts.map(o => o.production_order_id)).size}</div>
            <div className="card-detail">{t('productionCostingOverview.ordersInPeriod')}</div>
          </div>
        </div>
      )}

      {/* Top Cost Drivers */}
      {topDrivers.length > 0 && (
        <div className="costing-section">
          <h3>{t('productionCostingOverview.topCostDrivers')}</h3>
          <table className="costing-table">
            <thead>
              <tr>
                <th>{t('common:ui.material')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.qtyUsed')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.totalCost')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.pctOfTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {topDrivers.map((d, i) => (
                <tr key={i}>
                  <td><strong>{d.item_name || d.material_name}</strong><br/><span style={{ color: 'var(--text-secondary)', fontSize: '0.85em' }}>{d.item_code || ''}</span></td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(d.total_quantity || 0).toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(d.total_cost)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtPct(d.percentage_of_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Waste Breakdown */}
      {wasteCosts && wasteCosts.waste_by_material && wasteCosts.waste_by_material.length > 0 && (
        <div className="costing-section">
          <h3>{t('productionCostingOverview.wasteBreakdown')}</h3>
          <table className="costing-table">
            <thead>
              <tr>
                <th>{t('common:ui.product')}</th>
                <th>{t('productionCostingOverview.columns.poNumber')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.plannedQty')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {wasteCosts.waste_by_material.map((w, i) => (
                <tr key={i}>
                  <td>{w.material_name}</td>
                  <td>{w.po_number}</td>
                  <td style={{ textAlign: 'right' }}>{parseFloat(w.quantity || 0).toLocaleString()} {w.unit_of_measure}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(w.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-Order Costs */}
      {orderCosts.length > 0 && (
        <div className="costing-section">
          <h3>{t('productionCostingOverview.costByOrder')}</h3>
          <table className="costing-table">
            <thead>
              <tr>
                <th>{t('productionCostingOverview.columns.poNumber')}</th>
                <th>{t('common:ui.product')}</th>
                <th>{t('common:ui.customer')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.materialCost')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.wasteIncluded')}</th>
                <th style={{ textAlign: 'right' }}>{t('productionCostingOverview.columns.unitCost')}</th>
                <th>{t('common:ui.status')}</th>
              </tr>
            </thead>
            <tbody>
              {orderCosts.map((o, i) => (
                <tr key={i}>
                  <td><strong>{o.po_number}</strong></td>
                  <td>{o.product_name || '-'}</td>
                  <td>{o.customer_name || '-'}</td>
                  <td style={{ textAlign: 'right' }}><strong>{fmt(o.total_material_cost)}</strong></td>
                  <td style={{ textAlign: 'right' }}>{fmt(o.waste_cost)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(o.material_cost_per_unit)}</td>
                  <td>
                    <span className={`status-badge status-${(o.status || '').toLowerCase()}`}>
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!overview && orderCosts.length === 0 && (
        <div className="sales-costing-overview empty">
          <p>{t('productionCostingOverview.noData')}</p>
        </div>
      )}
    </div>
  );
};

export default ProductionCostingOverview;
